import { useEffect, useRef, useState } from "react";
import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
} from "@/components/common/surfaceClasses";
import { AdsAssistantAggregationDesktopLayout } from "@/components/ads-assistant/AdsAssistantAggregationDesktopLayout";
import { AdsAssistantAggregationManualForm } from "@/components/ads-assistant/AdsAssistantAggregationManualForm";
import { AdsAssistantAggregationIndexedRegion } from "@/components/ads-assistant/AdsAssistantAggregationIndexedRegion";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { AdsAssistantEngagementBars } from "@/components/ads-assistant/AdsAssistantEngagementBars";
import { AdsAssistantManualIngestionBox } from "@/components/ads-assistant/AdsAssistantManualIngestionBox";
import { AdsAssistantGenerateSimilarDialog } from "@/components/ads-assistant/AdsAssistantGenerateSimilarDialog";
import { AdsAssistantPostAttributionDialog } from "@/components/ads-assistant/AdsAssistantPostAttributionDialog";
import { useAdsAssistantAggregation } from "@/components/ads-assistant/useAdsAssistantAggregation";
import { useAdsAssistantEngagement } from "@/components/ads-assistant/useAdsAssistantEngagement";
import { ADS_MANUAL_FORM_ID } from "@/components/ads-assistant/adsActivityCardIds";
import type { AdsGenerateOpen } from "@/components/ads-assistant/AdsAssistantAggregationPostsList";

export type Props = {
  scopedOrgId: string | null;
  embedded?: boolean;
  variant?: "full" | "browse";
};

/**
 * Manual ingestion + index of posts. The right-rail "Org activity" strip was
 * retired in Phase 6 (see docs/planning/org_activity_top_level_mode.md);
 * today's goal / streak / achievements now live in the dedicated Activity
 * primary mode. Suggestion stats keep rendering above the indexed posts in
 * the "Suggestion" workspace area.
 */
export function AdsAssistantAggregationSection({
  scopedOrgId,
  embedded,
  variant = "full",
}: Props) {
  const vm = useAdsAssistantAggregation(scopedOrgId, variant);
  const engagement = useAdsAssistantEngagement(scopedOrgId, {
    loadSuggestionStats: variant === "browse",
  });
  const platformSelectRef = useRef<HTMLSelectElement | null>(null);
  const [celebrate, setCelebrate] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState<AdsGenerateOpen | null>(null);
  const [attributionPost, setAttributionPost] = useState<import("@/lib/adsAssistantClient").AdsAssistantSocialPostRow | null>(
    null,
  );
  const [indexedPostsOpen, setIndexedPostsOpen] = useState(false);

  useEffect(() => {
    if (!celebrate) return;
    const id = window.setTimeout(() => setCelebrate(null), 8000);
    return () => window.clearTimeout(id);
  }, [celebrate]);

  // Indexed posts default collapsed (dev-sync 5c60f6c7). Users expand via
  // "Search previous posts" on both mobile and desktop.
  const refreshAll = () => {
    void vm.load();
    void engagement.reloadEngagement();
  };

  if (!scopedOrgId) {
    return embedded ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Choose an organization to load indexed posts.</p>
    ) : (
      <section
        className={`${cardSurfaceClass} px-4 py-3`}
        aria-labelledby="ads-aggregation-heading"
      >
        <h3 id="ads-aggregation-heading" className={`text-sm font-semibold ${cardTitleTextClass}`}>
          Posts &amp; ads index
        </h3>
        <p className={`mt-2 text-sm ${cardMutedTextClass}`}>Choose an organization to load indexed posts.</p>
      </section>
    );
  }

  const heading =
    !embedded && variant !== "browse" ? (
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="ads-aggregation-heading" className={`text-sm font-semibold ${cardTitleTextClass}`}>
            Posts &amp; ads index
          </h3>
          <p className={`mt-1 text-xs max-w-prose ${cardMutedTextClass}`}>
            Paste copy as it ran on the platform, note what the creative shows or should show, choose categories and an
            optional template. Users with marketing, trainer, or Ads guidance editor roles can label examples good or bad.
            Analysis summarizes copy and flags policy substring hits. Saved posts count toward the Leads tab{" "}
            <strong className={`font-medium ${cardTitleTextClass}`}>Daily checklist → Log ads</strong> when your org enables ad
            logging in Location Tools.
          </p>
        </div>
        {variant === "full" ? (
          <AdsHelpHint label="Reading the list">
            Rows show platform, template badge, categories, AI summary, and policy flags. Imported rows share the same
            list. Open the <strong>Activity</strong> mode for today&apos;s goal, streak, and achievements.
          </AdsHelpHint>
        ) : null}
      </div>
    ) : null;

  const banners = (
    <>
      {celebrate ? (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-100">
          {celebrate}
        </p>
      ) : null}
      {vm.saveMsg ? <p className="text-sm text-emerald-800">{vm.saveMsg}</p> : null}
      {vm.error ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-950">
          {vm.error}
          <span className="mt-1 block text-xs text-amber-900/80">
            If tables are missing, run library DDL in <code className="rounded bg-amber-100/80 px-1">sql_schema.md</code>.
          </span>
        </p>
      ) : null}
    </>
  );

  // Left column at lg:+. Ingestion variant: the manual ingestion box
  // wrapping the form. Suggestion variant: the engagement stats panel
  // plus the explainer paragraph that used to sit between the form
  // and the indexed list. Mobile (<lg) renders the same content above
  // the indexed region — the layout component stacks left then right
  // vertically below the `lg:grid` breakpoint.
  const leftColumn = vm.showManualForm ? (
    <AdsAssistantManualIngestionBox
      scopedOrgId={scopedOrgId}
      showCrmAdvanced={vm.showSalesGhlSync}
      onAfterSync={refreshAll}
    >
      <AdsAssistantAggregationManualForm
        embeddedInShell
        formId={ADS_MANUAL_FORM_ID}
        platformSelectRef={platformSelectRef}
        canIngestPosts={vm.canIngestPosts}
        canQuality={vm.canQuality}
        canSeeSlotLabeledFields={vm.canSeeSlotLabeledFields}
        canSeeGraphExternalId={vm.canSeeGraphExternalId}
        canSeePublishedAtOverride={vm.canSeePublishedAtOverride}
        publishOverrideOpen={vm.publishOverrideOpen}
        setPublishOverrideOpen={vm.setPublishOverrideOpen}
        platformOptions={vm.platformOptions}
        platform={vm.platform}
        setPlatform={vm.setPlatform}
        publishedAt={vm.publishedAt}
        setPublishedAt={vm.setPublishedAt}
        platformCta={vm.platformCta}
        setPlatformCta={vm.setPlatformCta}
        externalId={vm.externalId}
        setExternalId={vm.setExternalId}
        body={vm.body}
        setBody={vm.setBody}
        creativeDescription={vm.creativeDescription}
        setCreativeDescription={vm.setCreativeDescription}
        categories={vm.categories}
        toggleCat={vm.toggleCat}
        selectedCats={vm.selectedCats}
        postTagDefs={vm.postTagDefs}
        selectedPostTags={vm.selectedPostTags}
        togglePostTag={vm.togglePostTag}
        templates={vm.templates}
        templateId={vm.templateId}
        setTemplateId={vm.setTemplateId}
        slotFillJsonText={vm.slotFillJsonText}
        setSlotFillJsonText={vm.setSlotFillJsonText}
        promotedExemplar={vm.promotedExemplar}
        setPromotedExemplar={vm.setPromotedExemplar}
        qualityRating={vm.qualityRating}
        setQualityRating={vm.setQualityRating}
        qualityNote={vm.qualityNote}
        setQualityNote={vm.setQualityNote}
        saving={vm.saving}
        onManualSubmit={vm.onManualSubmit}
        manualPostMediaFiles={vm.manualPostMediaFiles}
        setManualPostMediaFiles={vm.setManualPostMediaFiles}
      />
    </AdsAssistantManualIngestionBox>
  ) : (
    <>
      {variant === "browse" ? (
        <AdsAssistantEngagementBars
          stats={engagement.stats}
          loadSuggestionStats
          statsScope={engagement.statsScope}
          setStatsScope={engagement.setStatsScope}
          engagementError={engagement.engagementError}
        />
      ) : null}
      <p className="text-xs text-gray-600 dark:text-gray-400 max-w-prose">
        Browse indexed posts and coaching hints below. Switch to <strong>Ingestion</strong> to log posts. Trainers can add
        posts via the <strong>Feed import</strong> tab. Use <strong>Library</strong> to edit categories, templates, and
        policies.
      </p>
    </>
  );

  const rightColumn = (
    <AdsAssistantAggregationIndexedRegion
      vm={vm}
      scopedOrgId={scopedOrgId}
      indexedPostsOpen={indexedPostsOpen}
      setIndexedPostsOpen={setIndexedPostsOpen}
      onCelebration={setCelebrate}
      onReassignAttribution={setAttributionPost}
      onReload={refreshAll}
      onRequestGenerate={setGenerateOpen}
    />
  );

  return (
    <section
      className={embedded ? "" : `${cardSurfaceClass} px-4 py-3`}
      aria-labelledby={embedded ? undefined : "ads-aggregation-heading"}
    >
      {heading}

      <AdsAssistantAggregationDesktopLayout
        banners={banners}
        leftColumn={leftColumn}
        rightColumn={rightColumn}
      />

      <AdsAssistantPostAttributionDialog
        post={attributionPost}
        onClose={() => setAttributionPost(null)}
        onSaved={refreshAll}
      />

      <AdsAssistantGenerateSimilarDialog
        open={!!generateOpen}
        templateId={generateOpen?.templateId ?? null}
        templateName={generateOpen?.templateName ?? null}
        socialPostId={generateOpen?.socialPostId ?? null}
        onClose={() => setGenerateOpen(null)}
      />
    </section>
  );
}
